import { camelCase, isEmpty } from 'lodash-es';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { paramCleaner } from '@/helpers/formatters';
import { getRequest, postRequest } from './request';

export const getRequisition = async (reqId, { hiringTeamSort = '-team' } = {}) => {
  const responseData = {};
  const failedRequests = [];
  const responseDefaultValue = {
    info: {},
    posts: [],
    hiringTeam: [],
    applicants: [],
    gender: {},
    source: {},
    table: {},
    authorized: false,
  };

  let unauthorized = false;

  const fetchBatch = async (batch) => {
    const promises = batch.map(({ path, params = {} }) => {
      const queryParams = new URLSearchParams(params).toString();

      return getRequest({ url: `/requisition/${path}/${reqId}?${queryParams}` });
    });
    const results = await Promise.allSettled(promises);

    results.forEach((response, i) => {
      const { path, label, defaultValue } = batch[i];
      const formattedPath = camelCase(path);

      // Create the response object
      if (response.status === 'fulfilled') {
        responseData[formattedPath] = response.value;
        return;
      }

      if (response.reason?.response?.status === 403) {
        unauthorized = true;
      } else {
        failedRequests.push(label);
      }

      responseData[formattedPath] = defaultValue;
    });
  };

  const batches = [
    [
      { path: 'info', label: 'info', defaultValue: {} },
    ],
    [
      { path: 'posts', label: 'job posts', defaultValue: [] },
      {
        path: 'hiring-team', label: 'hiring team', defaultValue: [], params: { sort: hiringTeamSort, excludeSharedRoles: true },
      },
      { path: 'applicants', label: 'applicants over time data', defaultValue: [] },
    ],
    [
      { path: 'gender', label: 'gender data', defaultValue: {} },
      { path: 'source', label: 'applicant source data', defaultValue: {} },
      { path: 'table', label: 'table data', defaultValue: {} },
    ],
  ];

  await fetchBatch(batches[0]);

  if (unauthorized) {
    useAppSnackbar().addErrorSnackbar({
      text: 'You do not have permission to view this requisition.',
    });

    return responseDefaultValue;
  }

  await fetchBatch(batches[1]);
  await fetchBatch(batches[2]);

  if (failedRequests.length) {
    useAppSnackbar().addErrorSnackbar({
      text: `There was an error fetching the requisition's ${failedRequests.join(', ')}`,
    });
  }

  responseData.authorized = true;

  return responseData;
};

export const getReqCandidates = async ({
  page, reqId, stage, sort = '-applied_at',
}) => {
  const candidates = await getRequest({ url: `/requisition/candidates/${reqId}?page=${page}&stage=${stage}&sort=${sort}` });
  return candidates;
};

export const getJobPosts = async (page, reqId) => {
  const jobPosts = await getRequest({ url: `/requisition/posts/${reqId}?page=${page}` });
  return jobPosts;
};

export const getHiringTeam = async ({
  page, reqId, sort = '-team', pageSize = 10, excludeSharedRoles = false,
}) => {
  const hiringTeam = await getRequest({ url: `/requisition/hiring-team/${reqId}?page=${page}&sort=${sort}&pageSize=${pageSize}&excludeSharedRoles=${excludeSharedRoles}` });
  return hiringTeam;
};

export const getRequisitions = async ({
  mode, params = {}, selectedFilters, selectedMyJobs, searchTerm,
}) => {
  const {
    sort, page, q, ...paramFilters
  } = params;

  const cleanedParams = paramCleaner(paramFilters);

  const searchParams = new URLSearchParams({ mode });
  if (typeof sort !== 'undefined') searchParams.append('sort', sort);
  if (typeof page !== 'undefined') searchParams.append('page', page);

  const {
    items = [],
    pagination = {},
    sorting = {},
    textFilter = {},
  } = await postRequest({
    url: `/requisition/documents?${searchParams}`,
    body: {
      filters: !isEmpty(selectedFilters) ? selectedFilters : cleanedParams,
      owned: selectedMyJobs,
      textFilter: searchTerm,
    },
  });

  return {
    documents: items,
    pagination,
    total: pagination?.totalDocuments,
    sorting,
    textFilter,
  };
};

export const sendRequisitionShareLinks = async (body) => postRequest({
  url: '/analytics/notify-req',
  body,
});

export const fetchActiveCandidatesCSV = async ({
  reqId, selectedStages, sort = '-applied_at',
}) => postRequest({
  url: `/requisition/candidates-download/${reqId}?sort=${sort}`,
  body: {
    stages: (selectedStages || [])
      .map((stage) => (stage === 'application' ? 'review' : stage)),
  },
  isCsv: true,
});
